import Chart from "react-apexcharts";

const FORMATADORES = {
  currency: (valor) =>
    valor === null || valor === undefined
      ? "—"
      : Number(valor).toLocaleString("pt-BR", {
          style: "currency",
          currency: "BRL",
        }),
  number: (valor) =>
    valor === null || valor === undefined
      ? "—"
      : Number(valor).toLocaleString("pt-BR", { maximumFractionDigits: 2 }),
};

const CORES_DAS_SERIES = ["#2D2D2B", "#8FC7A2", "#E07B39"];

const timestampLocal = (iso) => {
  const [ano, mes, dia] = String(iso).slice(0, 10).split("-").map(Number);
  return new Date(ano, mes - 1, dia).getTime();
};

const timestampDepoisDoFim = (iso) => {
  const [ano, mes, dia] = String(iso).slice(0, 10).split("-").map(Number);
  return new Date(ano, mes - 1, dia + 1).getTime();
};

// Folga à direita, em fração do intervalo da série. Medido na tela: o Apex só
// desenha o rótulo do último tick a partir de ~1% (corte em pixels); 2% dá
// margem para gráficos mais estreitos sem deixar vazio visível após o último
// ponto.
const FOLGA_DO_EIXO = 0.02;
// Faixa que ocupa menos que isso do eixo não comporta o rótulo na horizontal:
// ele passaria da borda do gráfico e sairia cortado.
const FRACAO_DE_FAIXA_ESTREITA = 0.2;

function RangeAnnotatedChart({ series, faixas = [], height = 350 }) {
  const temPontos =
    Array.isArray(series) &&
    series.some((serie) => Array.isArray(serie.data) && serie.data.length);
  if (!temPontos) {
    return null;
  }

  // O Apex descarta o rótulo do último tick quando ele cai na borda direita do
  // gráfico; uma folga pequena à direita faz o rótulo do último dia aparecer.
  const pontosX = series
    .flatMap((serie) => serie.data || [])
    .map((ponto) => ponto.x)
    .filter(Number.isFinite);
  const primeiroX = pontosX.length ? Math.min(...pontosX) : null;
  const ultimoX = pontosX.length ? Math.max(...pontosX) : null;
  const limiteDoEixo =
    ultimoX === null ? null : ultimoX + (ultimoX - primeiroX) * FOLGA_DO_EIXO;
  const faixaEstreita = (faixa) => {
    if (primeiroX === null || limiteDoEixo <= primeiroX) return false;
    const inicio = Math.max(timestampLocal(faixa.inicio), primeiroX);
    const fim = Math.min(timestampDepoisDoFim(faixa.fim), limiteDoEixo);
    return (
      (fim - inicio) / (limiteDoEixo - primeiroX) < FRACAO_DE_FAIXA_ESTREITA
    );
  };

  const formatadorDa = (serie) =>
    FORMATADORES[serie.formatter] || FORMATADORES.number;
  const seriesMonetarias = series
    .filter((serie) => serie.formatter === "currency")
    .map((serie) => serie.name);
  const seriesNumericas = series.filter(
    (serie) => serie.formatter !== "currency"
  );

  const options = {
    chart: {
      id: "apex-range-annotated-chart",
      animations: { enabled: false },
      events: {
        mounted: (chart) =>
          series
            .filter((serie) => serie.hidden)
            .forEach((serie) => chart.hideSeries(serie.name)),
      },
    },
    stroke: {
      curve: "smooth",
      width: series.map((serie) => (serie.name === "Margem (R$)" ? 2 : 3)),
    },
    colors: series.map(
      (serie, indice) => serie.color || CORES_DAS_SERIES[indice]
    ),
    legend: { show: series.length > 1 },
    xaxis: {
      type: "datetime",
      title: { text: "Data" },
      labels: { datetimeUTC: false, format: "dd/MM" },
      ...(limiteDoEixo === null ? {} : { max: limiteDoEixo }),
    },
    yaxis: [
      ...(seriesMonetarias.length
        ? [
            {
              seriesName: seriesMonetarias,
              min: 0,
              forceNiceScale: true,
              title: { text: "Faturamento e margem (R$)" },
              labels: { formatter: FORMATADORES.currency },
            },
          ]
        : []),
      ...seriesNumericas.map((serie) => ({
        seriesName: serie.name,
        opposite: true,
        min: 0,
        forceNiceScale: true,
        title: { text: serie.name },
        labels: { formatter: formatadorDa(serie) },
      })),
    ],
    tooltip: {
      x: { format: "dd/MM/yyyy" },
      y: series.map((serie) => ({
        // Série suavizada traz o valor de cada dia em `diario` (mesmo índice).
        formatter: (valor, { dataPointIndex } = {}) => {
          const formatar = formatadorDa(serie);
          const doDia = serie.diario?.[dataPointIndex];
          return doDia === undefined || doDia === null
            ? formatar(valor)
            : `${formatar(valor)} · no dia: ${formatar(doDia)}`;
        },
      })),
    },
    annotations: {
      xaxis: faixas.map((faixa) => ({
        x: timestampLocal(faixa.inicio),
        x2: timestampDepoisDoFim(faixa.fim),
        fillColor: faixa.cor,
        opacity: 0.12,
        borderColor: faixa.cor,
        label: {
          text: faixa.rotulo,
          orientation: faixaEstreita(faixa) ? "vertical" : "horizontal",
          borderColor: faixa.cor,
          style: {
            color: "#000000",
            background: "var(--ouran-card-header-bg)",
            fontSize: "12px",
          },
        },
      })),
    },
  };

  return (
    <Chart
      options={options}
      series={series.map((serie) => ({ name: serie.name, data: serie.data }))}
      type="line"
      height={height}
    />
  );
}

export default RangeAnnotatedChart;
